import { getAllQuestionnaireAnswers } from "@medplum/core";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { isEmpty } from "lodash";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { CPTCodeQuestionsLinkIds } from "@/encounters/constants/cpt-code";
import { CurrentAndPreviousQuestionnaireResponses } from "@/encounters/types/procedures";

type EncounterProceduresDiffCardProps = {
  currentAndPreviousQuestionnaireResponses: CurrentAndPreviousQuestionnaireResponses;
};

const EncounterProceduresDiffCard = ({
  currentAndPreviousQuestionnaireResponses,
}: EncounterProceduresDiffCardProps) => {
  const previousAnswers = currentAndPreviousQuestionnaireResponses.previousQuestionnaireResponse
    ? getAllQuestionnaireAnswers(
        currentAndPreviousQuestionnaireResponses.previousQuestionnaireResponse,
      )
    : {};
  const currentAnswers = currentAndPreviousQuestionnaireResponses.currentQuestionnaireResponse
    ? getAllQuestionnaireAnswers(
        currentAndPreviousQuestionnaireResponses.currentQuestionnaireResponse,
      )
    : {};

  let currentData = {
    cptCode: "",
    cptCodeNotes: "",
    cptDiagnosesFormset: [] as string[],
  };

  const previousCptCode = previousAnswers[CPTCodeQuestionsLinkIds.Code]?.[0]?.valueCoding?.code
    ? `${previousAnswers[CPTCodeQuestionsLinkIds.Code]?.[0]?.valueCoding?.code} - ${previousAnswers[CPTCodeQuestionsLinkIds.Code]?.[0]?.valueCoding?.display}`
    : "";

  const previousData = {
    cptCode: previousCptCode,
    cptCodeNotes: previousAnswers[CPTCodeQuestionsLinkIds.Notes]?.[0]?.valueString,
    cptDiagnosesFormset: previousAnswers[CPTCodeQuestionsLinkIds.DiagnosesFormsetDiagnosis]?.map(
      (item) => `${item.valueCoding?.code} - ${item.valueCoding?.display}`,
    ),
  };

  if (!isEmpty(currentAnswers)) {
    currentData = {
      cptCode: `${currentAnswers[CPTCodeQuestionsLinkIds.Code]?.[0]?.valueCoding?.code} - ${currentAnswers[CPTCodeQuestionsLinkIds.Code]?.[0]?.valueCoding?.display}`,
      cptCodeNotes: currentAnswers[CPTCodeQuestionsLinkIds.Notes]?.[0]?.valueString ?? "",
      cptDiagnosesFormset: currentAnswers[CPTCodeQuestionsLinkIds.DiagnosesFormsetDiagnosis]?.map(
        (item) => `${item.valueCoding?.code} - ${item.valueCoding?.display}`,
      ),
    };
  }

  return (
    <Group grow align="stretch">
      <Paper p="md" withBorder>
        <Stack>
          <Title mb="lg" order={4} c="gray.6">
            Previous
          </Title>
          <Stack gap="md">
            <Stack gap="0">
              <TextWrapper fw={600}>Procedure Code</TextWrapper>
              <WordsDiff
                showRemoved
                current={currentData.cptCode}
                previous={previousData.cptCode}
              />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Notes</TextWrapper>
              <WordsDiff
                showRemoved
                current={currentData.cptCodeNotes}
                previous={previousData.cptCodeNotes}
              />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Diagnoses</TextWrapper>
              <WordsDiff
                showRemoved
                current={currentData.cptDiagnosesFormset?.join(", ")}
                previous={previousData.cptDiagnosesFormset?.join(", ")}
              />
            </Stack>
          </Stack>
        </Stack>
      </Paper>
      <Paper p="md" withBorder>
        <Stack>
          <Title mb="lg" order={4} c="gray.6">
            Amended
          </Title>
          <Stack gap="md">
            <Stack gap="0">
              <TextWrapper fw={600}>Procedure Code</TextWrapper>
              <WordsDiff current={currentData.cptCode} previous={previousData.cptCode} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Notes</TextWrapper>
              <WordsDiff current={currentData.cptCodeNotes} previous={previousData.cptCodeNotes} />
            </Stack>
            <Stack gap="0">
              <TextWrapper fw={600}>Diagnoses</TextWrapper>
              <WordsDiff
                current={currentData.cptDiagnosesFormset?.join(", ")}
                previous={previousData.cptDiagnosesFormset?.join(", ")}
              />
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Group>
  );
};

export default EncounterProceduresDiffCard;
